import { RestaurantTableShape } from '../../../../types/restaurant/RestaurantTableTypes';

interface Props {
  shape: RestaurantTableShape;
  /** Cantidad de sillas a dibujar alrededor (según la capacidad de la mesa). */
  capacity?: number;
  className?: string;
}

/**
 * Ícono SVG de una mesa vista desde arriba (plano de salón): refleja su forma
 * —cuadrada / rectangular / redonda— con las sillas repartidas alrededor según la
 * capacidad. Los colores salen de las variables CSS `--rt-table-fill`,
 * `--rt-table-line`, `--rt-chair-fill` y `--rt-chair-line` (Configuración ›
 * Apariencia, vía `useRestaurantTableTheme`); si no están definidas usa el tema
 * por defecto (líneas negras, mesa café).
 */
const TABLE_FILL = 'var(--rt-table-fill, #C68A4E)';
const TABLE_LINE = 'var(--rt-table-line, #1C2434)';
const CHAIR_FILL = 'var(--rt-chair-fill, #E7E5E4)';
const CHAIR_LINE = 'var(--rt-chair-line, #1C2434)';

const clampCapacity = (value?: number) =>
  Math.max(1, Math.min(12, Math.round(value ?? 4)));

/** Una silla (respaldo hacia afuera): eje local +y = lado exterior. */
const Chair = ({ x, y, rot }: { x: number; y: number; rot: number }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot})`}>
    <rect
      x={-6}
      y={-5}
      width={12}
      height={10}
      rx={2.6}
      fill={CHAIR_FILL}
      stroke={CHAIR_LINE}
      strokeWidth={2}
    />
    <rect x={-6} y={3.6} width={12} height={3.4} rx={1.6} fill={CHAIR_LINE} />
  </g>
);

/** Reparte `count` sillas entre 4 lados según el orden de prioridad dado. */
const split = (count: number, order: number[]) => {
  const per = [0, 0, 0, 0];
  const base = Math.floor(count / 4);
  let extra = count % 4;
  order.forEach((side) => {
    per[side] = base;
  });
  for (const side of order) {
    if (extra <= 0) break;
    per[side] += 1;
    extra -= 1;
  }
  return per; // [top, bottom, left, right]
};

/** Posiciones equiespaciadas a lo largo de un tramo [from, to]. */
const spread = (n: number, from: number, to: number) => {
  if (n <= 0) return [];
  if (n === 1) return [(from + to) / 2];
  const step = (to - from) / (n - 1);
  return Array.from({ length: n }, (_, i) => from + i * step);
};

const squareChairs = (count: number) => {
  const [top, bottom, left, right] = split(count, [0, 1, 2, 3]);
  const chairs: { x: number; y: number; rot: number }[] = [];
  spread(top, 18, 46).forEach((x) => chairs.push({ x, y: 9, rot: 180 }));
  spread(bottom, 18, 46).forEach((x) => chairs.push({ x, y: 55, rot: 0 }));
  spread(left, 18, 46).forEach((y) => chairs.push({ x: 9, y, rot: 90 }));
  spread(right, 18, 46).forEach((y) => chairs.push({ x: 55, y, rot: -90 }));
  return chairs;
};

const rectangleChairs = (count: number) => {
  const ends = count >= 6 ? 1 : 0;
  const rem = Math.max(0, count - ends * 2);
  const top = Math.ceil(rem / 2);
  const bottom = Math.floor(rem / 2);
  const chairs: { x: number; y: number; rot: number }[] = [];
  spread(top, 15, 49).forEach((x) => chairs.push({ x, y: 12, rot: 180 }));
  spread(bottom, 15, 49).forEach((x) => chairs.push({ x, y: 52, rot: 0 }));
  if (ends) {
    chairs.push({ x: 6, y: 32, rot: 90 });
    chairs.push({ x: 58, y: 32, rot: -90 });
  }
  return chairs;
};

const roundChairs = (count: number) => {
  const n = Math.min(count, 10);
  return Array.from({ length: n }, (_, i) => {
    const deg = -90 + (i * 360) / n;
    const rad = (deg * Math.PI) / 180;
    return {
      x: 32 + 22 * Math.cos(rad),
      y: 32 + 22 * Math.sin(rad),
      rot: deg - 90,
    };
  });
};

const TableShapeIcon = ({
  shape,
  capacity,
  className = 'h-6 w-6',
}: Props) => {
  const count = clampCapacity(capacity);

  let chairs: { x: number; y: number; rot: number }[];
  let top: JSX.Element;

  if (shape === 'ROUND') {
    chairs = roundChairs(count);
    top = (
      <circle
        cx={32}
        cy={32}
        r={14}
        fill={TABLE_FILL}
        stroke={TABLE_LINE}
        strokeWidth={2.5}
      />
    );
  } else if (shape === 'RECTANGLE') {
    chairs = rectangleChairs(count);
    top = (
      <rect
        x={10}
        y={22}
        width={44}
        height={20}
        rx={3}
        fill={TABLE_FILL}
        stroke={TABLE_LINE}
        strokeWidth={2.5}
      />
    );
  } else {
    chairs = squareChairs(count);
    top = (
      <rect
        x={18}
        y={18}
        width={28}
        height={28}
        rx={3}
        fill={TABLE_FILL}
        stroke={TABLE_LINE}
        strokeWidth={2.5}
      />
    );
  }

  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {chairs.map((c, i) => (
        <Chair key={i} x={c.x} y={c.y} rot={c.rot} />
      ))}
      {top}
    </svg>
  );
};

export default TableShapeIcon;
